import { useState, useEffect } from 'react';
import CommentSection from './CommentSection.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { socialAPI } from '../services/api.ts';

interface Post {
  id: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
    user_type?: string;
  };
  content: string;
  image?: string;
  media_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  liked_by_user: boolean;
}

interface FeedProps {
  onNavigateToProfile?: (userId: string) => void;
}

type FeedTab = 'all' | 'following' | 'mutual';

export default function Feed({ onNavigateToProfile }: FeedProps) {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FeedTab>('all');

  useEffect(() => {
    fetchPosts();
  }, [activeTab]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'all':
          response = await socialAPI.getPosts(); // Fetch all posts
          break;
        case 'following':
          response = await socialAPI.getFollowingPosts(); // Fetch posts from users I'm following
          break;
        case 'mutual':
          response = await socialAPI.getMutualPosts(); // Fetch posts from mutual follows
          break;
        default:
          response = await socialAPI.getPosts();
      }
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    try {
      if (post.liked_by_user) {
        await socialAPI.unlikePost(postId);
      } else {
        await socialAPI.likePost(postId);
      }

      setPosts(posts.map(p =>
        p.id === postId
          ? {
            ...p,
            likes_count: post.liked_by_user ? p.likes_count - 1 : p.likes_count + 1,
            liked_by_user: !post.liked_by_user
          }
          : p
      ));
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const handleToggleComments = (postId: string) => {
    setOpenComments(openComments === postId ? null : postId);
  };

  const handleCommentAdded = (postId: string) => {
    setPosts(posts.map(p =>
      p.id === postId
        ? { ...p, comments_count: p.comments_count + 1 }
        : p
    ));
  };

  const handleProfileClick = (userId: string) => {
    if (onNavigateToProfile && userId !== user?.id) {
      onNavigateToProfile(userId);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  const isTrainer = (userType?: string) => {
    return userType === 'trainer';
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="animate-pulse space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-white/40">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-1/4 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/6"></div>
                </div>
              </div>
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Tab Navigation */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg mb-6 p-2">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'all'
                ? 'bg-[#9CAF88] text-white shadow-md'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            All Posts
          </button>
          <button
            onClick={() => setActiveTab('following')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'following'
                ? 'bg-[#9CAF88] text-white shadow-md'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            Following
          </button>
          <button
            onClick={() => setActiveTab('mutual')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'mutual'
                ? 'bg-[#9CAF88] text-white shadow-md'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            Mutual
          </button>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-6">
        {posts.length === 0 ? (
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-8 border border-white/40 text-center">
            <p className="text-gray-600">
              {activeTab === 'all' && 'No posts yet. Be the first to share your fitness journey!'}
              {activeTab === 'following' && "You're not following anyone yet. Start following users to see their posts!"}
              {activeTab === 'mutual' && "No mutual connections yet. Connect with others who follow you back!"}
            </p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg overflow-hidden">
              {/* Post Header */}
              <div className="p-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-12 h-12 bg-gray-300 rounded-full overflow-hidden cursor-pointer hover:ring-2 hover:ring-green-400 transition-all"
                      onClick={() => handleProfileClick(post.author.id)}
                    >
                      {post.author.profile_picture ? (
                        <img
                          src={post.author.profile_picture}
                          alt={post.author.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-green-300 to-green-500 flex items-center justify-center text-white font-bold">
                          {post.author.first_name[0]}{post.author.last_name[0]}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span
                          className="text-gray-800 cursor-pointer hover:text-[#9CAF88] transition-colors"
                          onClick={() => handleProfileClick(post.author.id)}
                        >
                          {post.author.first_name} {post.author.last_name}
                        </span>
                        {isTrainer(post.author.user_type) && (
                          <span className="bg-[#9CAF88] text-[#FEFCF8] text-xs px-2 py-1 rounded-full">
                            Trainer
                          </span>
                        )}
                      </div>
                      <span className="text-gray-600 text-sm">
                        <span
                          className="cursor-pointer hover:text-[#9CAF88] transition-colors"
                          onClick={() => handleProfileClick(post.author.id)}
                        >
                          @{post.author.username}
                        </span> • {formatTimeAgo(post.created_at)}
                      </span>
                    </div>
                  </div>
                  <button className="text-gray-500 hover:text-gray-700">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Post Content */}
              <div className="px-6 pb-4">
                <p className="text-gray-800">{post.content}</p>

                {(post.media_url || post.image) && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    {post.media_type === 'video' ? (
                      <video
                        src={post.media_url}
                        controls
                        className="w-full max-h-96 object-cover"
                        poster={post.thumbnail_url}
                      />
                    ) : (
                      <img
                        src={post.media_url || post.image}
                        alt="Post content"
                        className="w-full max-h-96 object-cover"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Post Actions */}
              <div className="px-6 py-4 border-t border-white/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <button
                      onClick={() => handleLike(post.id)}
                      className={`flex items-center space-x-2 transition-colors ${post.liked_by_user ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                        }`}
                    >
                      <svg className="h-5 w-5" fill={post.liked_by_user ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span>{post.likes_count}</span>
                    </button>
                    <button
                      onClick={() => handleToggleComments(post.id)}
                      className={`flex items-center space-x-2 transition-colors ${openComments === post.id ? 'text-[#D2824C]' : 'text-gray-500 hover:text-[#E6A756]'
                        }`}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span>{post.comments_count}</span>
                    </button>
                  </div>
                  <button className="text-gray-500 hover:text-[#9CAF88] transition-colors">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Comment Section */}
              <CommentSection
                postId={post.id}
                isOpen={openComments === post.id}
                onClose={() => setOpenComments(null)}
                initialCommentsCount={post.comments_count}
                onCommentAdded={() => handleCommentAdded(post.id)}
                onNavigateToProfile={onNavigateToProfile}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}